import { mergeMessages, maxRealSeq } from "../../utils/commsMessageMerge";

/**
 * Module-level cache of the newest messages per room, fed by the always-on
 * room watchers (RoomsUnreadProvider) and the open thread (useRoomMessages).
 *
 * Purpose: when the user navigates back to a conversation, it renders
 * instantly from this cache while the authoritative fetch reconciles in the
 * background — and messages received while browsing OTHER pages are already
 * here. This is a plain module singleton (same pattern as useCommsIdentity's
 * name cache) so appends in background rooms never re-render the app tree;
 * only subscribers of that specific room are notified.
 *
 * Memory bounds: newest MAX_MESSAGES_PER_ROOM messages per room, at most
 * MAX_ROOMS rooms (LRU-evicted). Optimistic messages are never stored.
 */

const MAX_MESSAGES_PER_ROOM = 50;
// One entry per client a coach might open. Raised from 30 so a roster larger
// than that stops silently losing its least-recently-viewed threads; matches
// MY_USER_PROFILE_MAX_ENTRIES so both caches cover the same roster.
const MAX_ROOMS = 60;

/**
 * @typedef {Object} CacheEntry
 * @property {Array<Object>} messages Seq-sorted, real (non-optimistic) only.
 * @property {boolean} contiguous False when a seq gap was observed, meaning
 *   the buffer may be missing messages and a reconcile fetch is required
 *   before trusting it as complete.
 * @property {number|null} syncedAt Epoch ms of the last authoritative FETCH.
 *   Realtime appends deliberately don't move it: "synced" means "reconciled
 *   with the server", which is what a last-synced indicator should report.
 * @property {number} touched Monotonic LRU counter.
 */

/** @type {Map<string, CacheEntry>} */
const rooms = new Map();
/** @type {Map<string, Set<Function>>} */
const subscribers = new Map();
let lruCounter = 0;

const keyOf = (roomId) => String(roomId);

const touch = (entry) => {
  lruCounter += 1;
  entry.touched = lruCounter;
};

const evictIfNeeded = () => {
  if (rooms.size <= MAX_ROOMS) {
    return;
  }
  let oldestKey = null;
  let oldestTouched = Infinity;
  for (const [key, entry] of rooms) {
    if (entry.touched < oldestTouched) {
      oldestTouched = entry.touched;
      oldestKey = key;
    }
  }
  if (oldestKey !== null) {
    rooms.delete(oldestKey);
  }
};

const getOrCreate = (roomId) => {
  const key = keyOf(roomId);
  let entry = rooms.get(key);
  if (!entry) {
    entry = { messages: [], contiguous: true, syncedAt: null, touched: 0 };
    rooms.set(key, entry);
    // Claim recency BEFORE evicting. A fresh entry starts at touched: 0, which
    // is lower than every existing entry, so evicting first would pick the
    // room just created — leaving the caller writing into a detached object
    // and silently dropping every new room once the cache is full.
    touch(entry);
    evictIfNeeded();
    return entry;
  }
  touch(entry);
  return entry;
};

const notifyRoom = (roomId) => {
  const subs = subscribers.get(keyOf(roomId));
  if (!subs) {
    return;
  }
  for (const cb of subs) {
    try {
      cb();
    } catch (err) {
      console.error("roomMessagesCache subscriber failed:", err);
    }
  }
};

/**
 * Merge one realtime message into a room's buffer. Skips optimistic
 * messages. Flags the entry as non-contiguous when a seq gap is detected so
 * consumers know a reconcile fetch is needed.
 * @param {string} roomId
 * @param {Object} msg Real message with integer seq.
 */
export const appendMessage = (roomId, msg) => {
  if (!roomId || !msg || msg._optimistic || !Number.isInteger(msg.seq)) {
    return;
  }
  const entry = getOrCreate(roomId);
  const knownMax = maxRealSeq(entry.messages);
  if (entry.messages.length > 0 && msg.seq > knownMax + 1) {
    entry.contiguous = false;
  }
  entry.messages = mergeMessages(entry.messages, [msg]).slice(
    -MAX_MESSAGES_PER_ROOM
  );
  notifyRoom(roomId);
};

/**
 * Replace/merge a freshly fetched newest-page into the buffer. A server page
 * is authoritative for its range, so the entry becomes contiguous again.
 * @param {string} roomId
 * @param {Array<Object>} messages
 */
export const seedMessages = (roomId, messages) => {
  if (!roomId || !Array.isArray(messages) || messages.length === 0) {
    return;
  }
  const entry = getOrCreate(roomId);
  const real = messages.filter((m) => !m._optimistic && Number.isInteger(m.seq));
  entry.messages = mergeMessages(entry.messages, real).slice(
    -MAX_MESSAGES_PER_ROOM
  );
  entry.contiguous = true;
  entry.syncedAt = Date.now();
  notifyRoom(roomId);
};

/**
 * Snapshot of a room's cached messages.
 *
 * `syncedAt` is null for a room that has only ever received realtime appends
 * (a background room the coach hasn't opened): there are messages, but nothing
 * has reconciled with the server yet.
 *
 * @param {string} roomId
 * @returns {{ messages: Array<Object>, contiguous: boolean, syncedAt: number|null }}
 */
export const getCachedMessages = (roomId) => {
  const entry = rooms.get(keyOf(roomId));
  if (!entry) {
    return { messages: [], contiguous: true, syncedAt: null };
  }
  touch(entry);
  return {
    messages: entry.messages,
    contiguous: entry.contiguous,
    syncedAt: entry.syncedAt,
  };
};

/**
 * Mark every room stale (e.g. after a websocket reconnect — events may have
 * been missed while disconnected).
 */
export const markAllStale = () => {
  for (const entry of rooms.values()) {
    entry.contiguous = false;
  }
};

/**
 * Subscribe to mutations of one room's buffer.
 * @param {string} roomId
 * @param {Function} cb
 * @returns {Function} Unsubscribe.
 */
export const subscribe = (roomId, cb) => {
  const key = keyOf(roomId);
  let subs = subscribers.get(key);
  if (!subs) {
    subs = new Set();
    subscribers.set(key, subs);
  }
  subs.add(cb);
  return () => {
    subs.delete(cb);
    if (subs.size === 0) {
      subscribers.delete(key);
    }
  };
};

/** Drop everything (logout safety). */
export const clear = () => {
  rooms.clear();
};
